/* App shell: login gate, loading, tabs, rendering. */
import './style.css';
import {State, me, ui} from './state.ts';
import {can, ROLE_LABEL} from './lib/model.ts';
import {fmtN} from './lib/time.ts';
import * as db from './db.ts';
import * as sync from './syncer.ts';
import {escapeHtml, installClientTips, toast, $} from './ui/common.ts';
import {closeCombo} from './ui/combo.ts';
import {renderLogin} from './ui/login.ts';
import {merged} from './ui/merged.ts';
import {refreshFs} from './ui/fullscreen.ts';
import {swipeBusy, whenSwipeEnds} from './ui/home.ts';
import {renderTimerTab, renderEntriesTab, wireEntries, resetEntriesView} from './ui/entries.ts';
import {renderTeamTab, wireTeam} from './ui/team.ts';
import {renderClientsTab, wireClients} from './ui/clients.ts';
import {renderReportsTab, wireReports, rptLoad, rptForUser, rptZoneReset} from './ui/reports.ts';
import {setMyTz, cachedTz, forgetTz, myTz, zoneName, tzChanges} from './zone.ts';
import {cachedAlso, forgetAlso, loadAlso} from './alsorun.ts';
import {renderDataTab, wireData} from './ui/data.ts';
import {installPullToRefresh} from './ui/pull.ts';
import * as updater from './updater.ts';
import {installSurprises, refreshSurprises, checkMilestones, replayOnRefresh, surprisesReady} from './surprises.ts';
import {installEggs} from './eggs.ts';
import {syncBirthdays, maybeSyncBirthdays, forgetBirthdays} from './birthdays.ts';
import {applyDblTap} from './ui/dbltap.ts';
import {installTheme} from './ui/theme.ts';
import {maybeWelcome, onSaved, closeWelcomeAll} from './welcome.ts';
applyDblTap();   // Settings → Double-tap zoom (per device; default On)
installTheme();   // Settings → Appearance: Light / Dark / System (per device; index.html already applied it before the first paint)

/* 'data' is shown as Settings (gear); the id stays 'data' so saved state and tests keep working.
   The Entries page is not in this bar: it is opened from the bottom of the Timer screen ("See all entries →"), and
   while it is open the bar shows Timer as the current tab. */
const TABS = [
  {id: 'timer', label: 'Timer', ic: '<img src="/icons/favicon.svg" class="tab-ic" alt="">'}, {id: 'team', label: 'Team', ic: '👤'},
  {id: 'clients', label: 'Clients', ic: '🏢'}, {id: 'reports', label: 'Reports', ic: '📊'}, {id: 'data', label: 'Settings', ic: '<svg class="tab-ic gear-ic" viewBox="0 0 24 24" aria-hidden="true"><path fill="#7d7f87" d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.47.47 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.74 8.87a.47.47 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.48.48 0 0 0-.12-.61l-2.01-1.58zM12 15.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2z"/></svg>'},
];
let phase: 'boot' | 'login' | 'loading' | 'blocked' | 'ready' = 'boot';
let loadMsg = '', blockedMsg = '';
const setStatus = (t: string) => { $('statusLine').textContent = t; };

function renderTabs() {
  const nav = $('tabs');
  // members have no Team tab (owner decision 2026-10-13: they see only themselves; reached anyway, Team shows just them)
  const tabs = TABS.filter(t => t.id !== 'team' || can(State.role, 'seeAll'));
  nav.innerHTML = phase !== 'ready' ? '' : tabs.map(t => `<button class="${(State.tab === 'entries' ? 'timer' : State.tab) === t.id ? 'active' : ''}" data-tab="${t.id}"><span class="ic">${t.ic}</span><span>${t.label}</span></button>`).join('');
  nav.querySelectorAll<HTMLElement>('button').forEach(b => b.onclick = () => { State.tab = b.dataset.tab!; render(); window.scrollTo(0, 0); });
}
function renderIdentity() {
  const el = $('identityLine'), m = me();
  // Refresh and Sign out are two separate buttons with room between them (a mis-tap must not sign anyone out)
  el.innerHTML = phase === 'ready' && m ? `<span class="who">${escapeHtml(m.name)} · ${escapeHtml(ROLE_LABEL[m.role] || '')}</span>
      <span class="hdr-acts"><button type="button" class="hdr-btn" id="refreshBtn" title="Refresh everything from the server now (on a phone you can also pull the page down)">↻ Refresh</button>
      <button type="button" class="hdr-btn quiet" id="signOut">Sign out</button></span>`
    : phase === 'blocked' ? '<span class="hdr-acts"><button type="button" class="hdr-btn quiet" id="signOut">Sign out</button></span>' : '';
  const so = document.getElementById('signOut');
  if (so) so.onclick = () => {
    const n = sync.pendingCount();
    if (!n && !confirm('Are you sure you want to sign out?')) return;
    // nothing is deleted: waiting changes are kept for this login on this device and sent at its next sign-in (syncer.stop)
    if (n && !confirm(`${n} change${n === 1 ? " hasn't" : "s haven't"} reached the server yet. ${n === 1 ? 'It stays' : 'They stay'} on this device and ${n === 1 ? 'is' : 'are'} sent when you sign in again. Sign out?`)) return;
    db.sb.auth.signOut();
  };
  const rf = document.getElementById('refreshBtn'); if (rf) rf.onclick = () => { replayOnRefresh(); toast('Refreshing…', 1500); void refreshAll().then(m => toast(m)); };   // (little surprises: the day's effect again)
}
let renderQueued = false;
function render() { if (renderQueued) return; renderQueued = true; requestAnimationFrame(() => { renderQueued = false; renderNow(); }); }
ui.render = render;
ui.reload = () => sync.catchUp({structure: true});
function renderNow() { renderApp(); refreshFs(); renderStatus(); if (phase !== 'boot' && phase !== 'loading') surprisesReady(); refreshSurprises(); }   // (little surprises: the header's doodle follows the person's day and zone)   // (the status line's "synced" time follows a changed time zone at once)   // the full-screen timer follows every redraw (or closes when there is no Timer card)
function renderApp() {
  renderTabs(); renderIdentity();
  const main = $('main');
  if (phase === 'login') { renderLogin(main); return; }
  if (phase === 'boot' || phase === 'loading') { main.innerHTML = `<div class="empty">${escapeHtml(loadMsg || 'Loading…')}</div>`; return; }
  if (phase === 'blocked') { main.innerHTML = `<div class="card"><h2>No access</h2><p class="muted-text">${escapeHtml(blockedMsg)}</p></div>`; return; }
  main.classList.toggle('nomoney', !can(State.role, 'seeMoney'));
  // a redraw must never lose typing: remember the field being typed in (value, cursor) and put it back afterwards
  const a = document.activeElement as HTMLInputElement | null;
  const typing = a && a.id && main.contains(a) && /^(INPUT|TEXTAREA)$/.test(a.tagName) && !/^(checkbox|radio|file)$/.test(a.type)
    ? {id: a.id, value: a.value, start: a.selectionStart, end: a.selectionEnd} : null;
  if (State.tab !== 'reports') rptZoneReset();   // Reports' "My time zone" switch is never kept: leaving Reports = Chicago again
  switch (State.tab) {
    case 'timer': main.innerHTML = renderTimerTab(); wireEntries(); break;
    case 'entries': main.innerHTML = renderEntriesTab(); wireEntries(); break;
    case 'team': main.innerHTML = renderTeamTab(); wireTeam(); break;
    case 'clients': main.innerHTML = renderClientsTab(); wireClients(); break;
    case 'reports': main.innerHTML = renderReportsTab(); wireReports(); wireEntries(); break;
    case 'data': main.innerHTML = renderDataTab(); wireData(); break;   // bookkeeper and members: only Shake to undo + the Shake test there
  }
  if (typing) {
    const el = document.getElementById(typing.id) as HTMLInputElement | null;
    if (el && main.contains(el) && !el.disabled) {
      if (el.value !== typing.value) { el.value = typing.value; el.dispatchEvent(new Event('input', {bubbles: true})); }
      el.focus();
      try { if (typing.start !== null) el.setSelectionRange(typing.start, typing.end); } catch { /* not a text field */ }
    }
  }
}

/* status line: live / syncing / offline / changes waiting to be saved */
function renderStatus() {
  if (phase !== 'ready') return;
  const n = sync.pendingCount(), offline = !navigator.onLine || sync.status.problem === 'Offline';
  const time = sync.status.lastOk ? new Date(sync.status.lastOk).toLocaleTimeString('en-US', {timeZone: myTz(), hour: 'numeric', minute: '2-digit'}) : '';
  const el = $('statusLine');
  el.textContent = n ? (offline ? `Offline · ${n} change${n === 1 ? '' : 's'} saved here, sent when back online`
      : sync.status.problem ? `Waiting to send ${n} change${n === 1 ? '' : 's'} (saved here) · could not reach the server`
      : `Saving ${n} change${n === 1 ? '' : 's'}…`)
    : offline ? 'Offline · showing the copy on this device'
    : sync.status.problem ? sync.status.problem
    : sync.status.syncing ? 'Syncing…'
    : (sync.status.live ? '● Live' : 'Connecting…') + (time ? ` · synced ${time}` : '');
  el.title = time ? 'Last synced ' + time + ' (' + zoneName() + ')' : '';
  el.className = 'status' + (n || offline || sync.status.problem ? ' warn' : sync.status.live ? ' live' : '');
  updater.poke();   // an update the person asked for waits for the changes still being sent
}
sync.hooks.render = () => softRender();
sync.hooks.status = renderStatus;
sync.hooks.toast = (m: string) => toast(m, 7000);
sync.hooks.prefs = (now: boolean) => { if (now || Date.now() - lastAlso > 60_000) void loadMyAlso(); maybeSyncBirthdays(now); };   // (and this person's birthday list: birthdays.ts)
sync.hooks.saved = onSaved;
sync.hooks.merged = notes => { merged(notes); render(); };   // migration 016: told, never asked
sync.hooks.blocked = () => { sync.stop(); phase = 'blocked'; blockedMsg = BLOCKED; render(); };
const BLOCKED = 'This login is not linked to an active team member — the profile may have been archived. If this is a mistake, ask the owner.';

/** redraw after a background update, without disturbing someone who is typing (waits until they leave the field) */
let softPending = false;
function softRender() {
  if (phase === 'ready' && swipeBusy()) { whenSwipeEnds(softRender); return; }   // a finger is sliding a row: redraw after it lets go
  const a = document.activeElement as HTMLElement | null;
  if (phase === 'ready' && a && $('main').contains(a) && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) {
    if (!softPending) { softPending = true; a.addEventListener('blur', () => { softPending = false; render(); }, {once: true}); }
    return;
  }
  render();
}

const LAST_USER = 'ot_last_user';   // who was signed in on this device (to keep working offline when the login cannot be refreshed)
let loading = false;
async function loadEverything(uid: string) {
  if (loading) return; loading = true;
  try {
    phase = 'loading'; loadMsg = 'Loading…'; render();
    try { localStorage.setItem(LAST_USER, uid); } catch { /* private mode */ }
    setMyTz(cachedTz(uid));   // this person's time zone as last seen on this device (first paint, offline); the database's follows
    cachedAlso(uid);          // the "Also running" switches likewise
    const r = await sync.start(uid, (n, total) => { loadMsg = `Loading recent entries… ${fmtN(n)} of ${fmtN(total)}`; render(); },
      () => { loadMsg = navigator.onLine ? 'Signing in…' : 'No connection — this device has no copy of your data yet. It loads as soon as there is a signal.'; render(); });
    if (r === 'stale') return;   // signed out (or started again) meanwhile: that call shows what is right
    if (r === 'blocked') { phase = 'blocked'; blockedMsg = BLOCKED; return; }
    phase = 'ready';
    rptForUser();   // Reports open on this person's own time
    setTimeout(() => checkMilestones(false, sync.pendingCount()), 8000);   // little surprises: milestone cheers (own hours; at most every 6 hours)
    void loadMyTz(uid); void loadMyAlso(); void syncBirthdays();   // (birthdays: only once the login is confirmed; offline the device copy is used)
    setTimeout(() => { if (phase === 'ready') void maybeWelcome(); }, 0);   // brand-new people: a welcome and a short guide (welcome.ts)
  } catch (e: any) {
    console.error(e); setStatus('load failed');
    phase = 'loading'; loadMsg = 'Could not load: ' + (e?.message || e) + ' — check the connection and reload the page.';
    // no copy on this device yet and no signal: it loads by itself once the signal is back
    if (!navigator.onLine) { loadMsg = 'No connection — this device has no copy of your data yet. It loads as soon as there is a signal.'; addEventListener('online', () => { if (phase === 'loading') void loadEverything(uid); }, {once: true}); }
  } finally { loading = false; render(); renderStatus(); }
}
/** this person's time zone from the database (it wins over the device's copy); offline: the copy stays */
async function loadMyTz(uid: string) {
  const meId = State.meId, asked = tzChanges(); if (!meId) return;
  try {
    const z = await db.myTimeZone(meId);
    if (State.meId !== meId || tzChanges() !== asked) return;   // signed out, or the person picked a zone meanwhile (that one wins)
    const before = myTz(); setMyTz(z, uid);
    if (myTz() !== before) render();
  } catch { /* offline: keep the device's copy */ }
}
/** the "Also running" switches from the database (they win over the device's copy); offline: the copy stays */
let lastAlso = 0;
async function loadMyAlso() {
  const meId = State.meId; if (!meId || !can(State.role, 'seeAll')) return;   // members never see the line: nothing to ask
  lastAlso = Date.now();
  try { if (await loadAlso(meId) && State.meId === meId) softRender(); } catch { /* offline: keep the device's copy */ }
}
/** pull down, or "Refresh": a newer version of the app is loaded (when nothing can be lost, see updater.ts), otherwise
    everything is refreshed from the server */
async function refreshAll(): Promise<string> {
  if (phase !== 'ready') { setTimeout(() => location.reload(), 200); return 'Reloading…'; }
  const upd = navigator.onLine ? await updater.applyNow() : '';
  if (upd === 'Loading the new version…') return upd;
  const ok = await sync.refreshNow();
  let u: string | null = null; try { u = localStorage.getItem(LAST_USER); } catch { /* ignore */ }
  if (ok && u) { await loadMyTz(u); await loadMyAlso(); }
  if (upd) return upd;   // a newer version is waiting: say why it did not load yet (the bar says it too)
  return ok ? 'Up to date' : navigator.onLine ? 'Could not reach the server — try again' : 'Offline — showing what is on this device';
}
function signedOut() {
  forgetBirthdays(State.meId);   // (a birthday list with changes not sent yet stays for this person's next sign-in)
  sync.stop(); resetEntriesView(); closeCombo(); closeWelcomeAll(); forgetTz(); forgetAlso(); rptZoneReset(); try { localStorage.removeItem(LAST_USER); } catch { /* ignore */ }
  phase = 'login'; Object.assign(State, {meId: null, role: null, members: [], clients: [], projects: [], tasks: [], entries: [], timers: [], assign: []}); setStatus(''); render();
}

/** what is going on right now, for the app's own updates (lib/update.ts safeToReload) */
function appMoment() {
  const a = document.activeElement as HTMLElement | null;
  const typing = !!a && (a.isContentEditable || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT'
    || a.tagName === 'INPUT' && !/^(checkbox|radio|button|submit|reset|file|range|color)$/.test((a as HTMLInputElement).type));
  return {
    ready: phase === 'ready',
    windowOpen: !!document.querySelector('#modalRoot .modal-backdrop, .combo-panel, .sug:not([hidden]), #wlCard, #wlGuide'),   // (the welcome and its guide too)
    fullScreen: !!document.querySelector('#fsRoot .fs-overlay'),
    // (a description typed on the Timer card but not started yet is only kept on screen: it counts as typing)
    typing: typing || !!document.querySelector<HTMLInputElement>('#timerDesc:not([data-running])')?.value, pending: sync.pendingCount(),
    gesture: phase === 'ready' && swipeBusy() || document.querySelector<HTMLElement>('.ptr')?.dataset.state === 'busy',
    undoOffered: !!document.getElementById('undoBar'),
    loginTyped: phase !== 'ready' && [...document.querySelectorAll<HTMLInputElement>('#main input')].some(i => !!i.value),
  };
}
function boot() {
  rptLoad(); installClientTips(); sync.installTriggers(); installPullToRefresh(() => { replayOnRefresh(); return refreshAll(); });   // (pulled down: the day's effect again, like Refresh)
  updater.install({moment: appMoment, flush: () => sync.flush()});
  let last0: string | null = null; try { last0 = localStorage.getItem(LAST_USER); } catch { /* ignore */ }
  const saved0 = db.storedLoginUser();
  if (saved0 && saved0 === last0) setMyTz(cachedTz(saved0));   // the person's zone from this device at once (offline too): the surprises' first look is their own day
  installSurprises(); installEggs();   // (the Easter egg hunt: Good Friday to Easter Monday)
  render();   // "Loading…" at once: before, nothing was drawn until the login library's first answer (owner's screenshot 2026-10-30: offline, an empty screen under the header)
  if (!db.configured) { phase = 'blocked'; blockedMsg = 'The site is missing its Supabase settings (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).'; render(); return; }
  let lastUser: string | null = null;
  // Open at once from this device's copy when this device's saved login is the person whose data it keeps: nothing waits
  // for the network (owner report 2026-10-29: offline overnight, the expired login held the screen for half a minute).
  // Until the login library confirms the login (below), nothing is sent or asked; changes wait in the queue.
  if (saved0 && saved0 === last0) { lastUser = saved0; void loadEverything(saved0); }
  db.sb.auth.onAuthStateChange((ev, session) => {
    if (ev === 'SIGNED_OUT') { lastUser = null; sync.setSignedIn(false); signedOut(); return; }   // only a real sign-out forgets the device copy
    const u = session?.user.id ?? null;
    if (u) {
      sync.setSignedIn(true);                // also after a token refresh: reconnects and catches up
      if (u === lastUser) return;
      lastUser = u; setTimeout(() => loadEverything(u), 0);   // never call Supabase inside this callback (it can deadlock)
      return;
    }
    // No session, but not signed out: the login could not be refreshed (no network). Keep working from this device's
    // copy; changes wait in the queue, and everything resumes when the login is refreshed.
    sync.setSignedIn(false);
    if (lastUser) return;
    let last: string | null = null; try { last = localStorage.getItem(LAST_USER); } catch { /* ignore */ }
    if (last) { lastUser = last; setTimeout(() => loadEverything(last!), 0); return; }
    phase = 'login'; render();
  });
}
boot();
// home-screen app: opens without network (see public/sw.js). Service workers need https (or localhost for testing).
if ('serviceWorker' in navigator && (location.protocol === 'https:' || location.hostname === 'localhost')) navigator.serviceWorker.register('/sw.js').catch(() => {});
